// =============================================================================
// ui/tabs/BestOpps.tsx — the setups you saw, taken or not.
// -----------------------------------------------------------------------------
// A FREE-TIER tab (the upgrade card: "Best Opps and Review moved to Free"), so a
// prospect can meet it before paying anything. Until this landed it rendered
// "Not ported yet" in the client that launches.
//
// 🚨 THE HEADING SAYS "of the Day" AND THE TAB IS NOT SCOPED TO A DAY — not to
// the global date range, not to accounts, not to anything. Josh's screenshot
// showed May entries under a `This week` filter. The wording is beta's and is
// kept for parity; the SCOPE is deliberately the whole collection. See
// `domain/bestOpps`.
// =============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
// ⚠ ONE DOOR. `ui/` may not import `domain/` (D47), and the io modules are
// reached through the same seam so scope is decided in one place.
import {
  fetchBestOpps, fetchTradesOn, newBestOpp, pickablePatterns, removeBestOpp,
  selectBestOppRows, storeBestOpp,
  type BestOpp,
} from '../../selectors/bestOpps';
import { DEFAULT_TZ } from '../../selectors/population';
import { usePreferences } from '../state/preferences';
import { useTabMemoryDraft } from '../state/tabMemory';
import { useSupabase } from '../state/supabase';
import { useAuthUser } from '../state/useAuthUser';
import { useContracts } from '../state/contracts';
import { usePatterns } from '../state/patterns';
import { ScreenshotGrid } from '../media/ScreenshotGrid';
import { moneyPlain } from '../format';

const GRADES = ['A+', 'A', 'B', 'C', 'D', 'F'] as const;

function todayIso(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** A client-side id, matching beta's `uid()` shape closely enough to be opaque. */
function newId(): string {
  return `bo_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

export function BestOppsTab() {
  const { sb } = useSupabase();
  const { user } = useAuthUser();
  const { patterns } = usePatterns();
  const { prefs } = usePreferences();
  // ⚠ The user's own day boundary, so this menu agrees with Day Overview and the
  // Calendar about which trades belong to a date. `calendar` until preferences
  // land — the same default the rest of the client reads.
  const mode = prefs?.dayBoundaryMode ?? 'calendar';
  // ⚠ PAIRED WITH THE LINE ABOVE, and only safe because of it: when `prefs`
  // has not loaded, `mode` is 'calendar' and the timezone is never consulted.
  const sessionTz = prefs?.sessionTimezone ?? DEFAULT_TZ;
  const fetchDayTrades = useCallback(
    (date: string) => fetchTradesOn(sb, date, mode, sessionTz),
    [sb, mode],
  );
  // ⚠ Read at the TOP, never from inside the conditional modal JSX below — a hook
  // called there runs on some renders and not others, which React rejects.
  const { contracts } = useContracts();
  const [opps, setOpps] = useState<readonly BestOpp[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<BestOpp | null>(null);

  const reload = useCallback(() => {
    let live = true;
    fetchBestOpps(sb)
      .then((rows) => { if (live) { setOpps(rows); setError(null); } })
      // ⚠ A failed read must NOT fall through to the empty state — "No best opps
      // logged yet" would be a lie about content only the user can recreate.
      .catch((e: unknown) => { if (live) { setOpps(null); setError(String(e)); } });
    return () => { live = false; };
  }, [sb]);

  useEffect(() => reload(), [reload]);

  const patternName = useCallback((id: string): string | null => {
    if (!id) return null;
    return (patterns ?? []).find((p) => p.id === id)?.name ?? null;
  }, [patterns]);

  const cards = useMemo(() => selectBestOppRows(opps, patternName), [opps, patternName]);

  if (error) {
    return (
      <div className="card">
        <h3>Best Opps could not be loaded</h3>
        <p className="muted">{error}</p>
        <button className="btn" onClick={reload}>Try again</button>
      </div>
    );
  }
  if (!opps) return <div className="card"><p className="muted">Loading…</p></div>;

  return (
    <>
      <div className="section-header">
        {/* ⚠ Beta's wording, verbatim, including "of the Day" — which this tab
            does not honour. Changing the copy is a product decision, not a port
            decision; the mismatch is recorded in domain/bestOpps. */}
        <h3>Best Opps of the Day ({opps.length})</h3>
        <button
          className="btn primary"
          onClick={() => setEditing(newBestOpp(newId(), todayIso()))}
        >
          + Log Best Opp
        </button>
      </div>

      {!opps.length ? (
        <div className="empty">
          <p>No best opps logged yet.</p>
          <p className="muted" style={{ marginTop: 6 }}>
            At EOD, log 1–5 of the day&apos;s best setups whether you took them or not.
            Builds pattern recognition over time.
          </p>
        </div>
      ) : (
        <div className="opp-grid">
          {cards.map(({ opp, card }) => (
            <button
              key={card.id}
              type="button"
              className="card opp-card"
              onClick={() => setEditing(opp)}
            >
              <div className="row">
                <div className="grow">
                  <div className="opp-title">
                    {card.contract}{' '}
                    <span className="muted opp-when">{card.dateLabel} {card.timeLabel}</span>
                  </div>
                  <div className="muted opp-pattern">{card.patternLabel}</div>
                </div>
                <div className="row gap-4">
                  <span className={card.taken ? 'tag green' : 'tag'}>
                    {card.taken ? 'TAKEN' : 'SKIPPED'}
                  </span>
                  {card.grade ? <span className="tag gold">{card.grade}</span> : null}
                </div>
              </div>
              {/* ⚠ A DELIBERATE DEVIATION, recorded so it is not read as drift:
                  beta cuts at 160 characters with NOTHING after it, so a card ends
                  mid-word ("a clear value co") and looks like a data error rather
                  than a truncation. The ellipsis says which it is. Nothing else
                  about the string changes — same cut, same length. */}
              <div className="opp-reflection">
                {card.hasReflection
                  ? <>{card.reflection}{card.truncated ? '…' : ''}</>
                  : <span className="muted">(no reflection)</span>}
              </div>
              <div className="row gap-12 opp-foot">
                <span><span className="muted">EST RR</span> {card.estRR}</span>
                <span>
                  <span className="muted">RISK</span>{' '}
                  {card.estRiskDollar == null ? '—' : moneyPlain(card.estRiskDollar, 0)}
                </span>
                {card.screenshotCount ? (
                  <span className="muted">📷 {card.screenshotCount}</span>
                ) : null}
              </div>
            </button>
          ))}
        </div>
      )}

      {editing ? (
        /* 🚨 PATTERNS ARE FILTERED HERE, NOT IN THE MODAL, because this is where
           the full record still exists — the modal takes `{id, name}` and the
           `status` the rule reads is gone by then.

           ⚠ `keepId` is the opp AS OPENED, not the live edit. A pattern the user
           picks during this session is by definition offerable; the one that
           needs protecting is whatever was saved before, which may since have
           gone back into testing. See `pickablePatterns`.

           ⚠ A comment cannot sit between JSX attributes — it lives here for that
           reason, not by preference. Second time today. */
        <BestOppModal
          opp={editing}
          isNew={!(opps ?? []).some((o) => o.id === editing.id)}
          contracts={(contracts ?? []).map((c) => ({ symbol: c.symbol, name: c.name }))}
          patterns={pickablePatterns(patterns, editing.identity.patternId)
            .map((p) => ({ id: p.id, name: p.name }))}
          fetchTrades={fetchDayTrades}
          onClose={() => setEditing(null)}
          onSave={async (next) => {
            if (!user?.id) return 'You are signed out — sign in again before saving.';
            const r = await storeBestOpp(sb, user.id, next);
            if (!r.ok) return r.error;
            setEditing(null);
            reload();
            return null;
          }}
          onDelete={async (id) => {
            const r = await removeBestOpp(sb, id);
            if (!r.ok) return r.error;
            setEditing(null);
            reload();
            return null;
          }}
        />
      ) : null}
    </>
  );
}

/** What the "Linked Trade (same day)" menu needs from a trade, and no more. */
export interface LinkableTrade {
  id: string;
  contract: string | null;
  timeIn: string | null;
  netPnl: number | null;
}

interface ModalProps {
  opp: BestOpp;
  isNew: boolean;
  contracts: readonly { symbol: string; name: string }[];
  patterns: readonly { id: string; name: string }[];
  /**
   * That day's trades, for the link menu.
   *
   * ⚠ A FUNCTION, NOT A LIST, because the date is edited INSIDE this modal — a
   * prop computed by the parent would be one render stale every time the user
   * steps the day. Injectable for the same reason the rest of this modal takes
   * props: the suite reaches it directly, without standing up four providers.
   */
  fetchTrades?: ((date: string) => Promise<readonly LinkableTrade[]>) | undefined;
  onClose: () => void;
  onSave: (o: BestOpp) => Promise<string | null>;
  onDelete: (id: string) => Promise<string | null>;
}

/**
 * ⚠ EXPORTED FOR THE TESTS, deliberately. The risky parts of this tab are all in
 * here — the Est R:R refusal, the edit-only Delete, and the free-text zones — and
 * rendering it directly with props is how the rest of this suite reaches a modal
 * without standing up four providers to do it.
 */
export function BestOppModal({
  opp, isNew, contracts, patterns, fetchTrades, onClose, onSave, onDelete,
}: ModalProps) {
  // ── 🔴 THE DRAFT OUTLIVES THE TAB, BUT CANCEL STILL DISCARDS IT (TCR-80) ──
  //
  // Leaving the tab destroys this modal (D94) along with everything typed into
  // it — two free-text zones and five fields. ⚠ Autosave is deliberately NOT the
  // fix here: this is a modal with a Cancel, and writing on blur would make that
  // button a lie. The draft lives above the tab and only Save reaches the
  // database. Decided by Josh, 2026-10-06.
  //
  // ⚠ This sits WELL with the refusal note on the Save button below. That note
  // keeps the modal open on a rejected Est R:R so the typing is not lost; this
  // extends the same courtesy to walking away from the tab entirely.
  //
  // ⚠ Keyed per opp. ⚠ A reload loses it — `tabMemory` is session-scoped.
  const [v, setV, discardDraft] = useTabMemoryDraft<BestOpp>(`bestopps:draft:${opp.id}`, opp);

  /** Throw the draft away, then close — the × and Cancel. */
  const cancel = useCallback(() => { discardDraft(); onClose(); }, [discardDraft, onClose]);

  /**
   * The day's trades for the link menu.
   *
   * ⚠ `alive` IS NOT DECORATION. Step the date twice and the first read can land
   * after the second, leaving one day's trades under another day's heading —
   * every entry plausible, none of them right. `useDayJournal` carries the same
   * guard against the same race, with the same note.
   *
   * ⚠ A FAILED READ LEAVES THE MENU EMPTY RATHER THAN THROWING. Losing the
   * ability to link is a smaller harm than losing the form with an unsaved opp
   * in it, and the option that is already selected still renders — see below.
   */
  const [sameDay, setSameDay] = useState<readonly LinkableTrade[]>([]);
  useEffect(() => {
    if (!fetchTrades || !v.identity.date) { setSameDay([]); return undefined; }
    let alive = true;
    void fetchTrades(v.identity.date)
      .then((rows) => { if (alive) setSameDay(rows); })
      .catch(() => { if (alive) setSameDay([]); });
    return () => { alive = false; };
  }, [fetchTrades, v.identity.date]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const ident = (p: Partial<BestOpp['identity']>) =>
    setV((s) => ({ ...s, identity: { ...s.identity, ...p } }));
  const ctx = (p: Partial<BestOpp['context']>) =>
    setV((s) => ({ ...s, context: { ...s.context, ...p } }));
  const refl = (p: Partial<BestOpp['reflection']>) =>
    setV((s) => ({ ...s, reflection: { ...s.reflection, ...p } }));

  return (
    <div className="modal-scrim" role="dialog" aria-modal="true" aria-label={isNew ? 'New Best Opp' : 'Edit Best Opp'}>
      <div className="modal wide">
        <header className="modal-head">
          {/* ⚠ Matches AccountEditor's head exactly — a <b> and a plain .btn —
              rather than inventing a second modal-header idiom. */}
          <b>{isNew ? 'New Best Opp' : 'Edit Best Opp'}</b>
          <button type="button" className="btn" aria-label="Close" onClick={cancel}>×</button>
        </header>

        <div className="modal-body">
          <h4 className="bo-sec">IDENTITY</h4>
          <div className="form-grid cols-3">
            <label className="field">
              <span>Date</span>
              <input type="date" value={v.identity.date} onChange={(e) => ident({ date: e.target.value })} />
            </label>
            <label className="field">
              <span>Contract</span>
              <select value={v.identity.contract} onChange={(e) => ident({ contract: e.target.value })}>
                {contracts.map((c) => (
                  <option key={c.symbol} value={c.symbol}>{c.symbol} — {c.name}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Time Set Up</span>
              {/* ⚠ step=1 so the field offers SECONDS, as beta's does. */}
              <input type="time" step={1} value={v.identity.time} onChange={(e) => ident({ time: e.target.value })} />
            </label>
            <label className="field">
              <span>Pattern</span>
              <select value={v.identity.patternId} onChange={(e) => ident({ patternId: e.target.value })}>
                <option value="">(none)</option>
                {patterns.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <label className="field">
              <span>Grade</span>
              <select value={v.identity.grade} onChange={(e) => ident({ grade: e.target.value })}>
                <option value="">—</option>
                {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </label>
            {/* ── 🚨 THE LINKAGE SECTION BETA HAS AND THIS TAB DID NOT ──
                An opportunity is a note about a setup; linking it to the trade it
                BECAME is what turns "I saw this" into "I took this, and here is
                what it did". The column (`best_opps.trade_id`), the reader, the
                writer and `BestOpp.linkage.tradeId` all already existed — only
                the control was missing, so nothing below this line is new
                plumbing.

                ⚠ SCOPED TO THE OPP'S OWN DAY, and session-aware: see
                `fetchTradesOn` in `selectors/bestOpps.ts` for why that departs
                from beta, which filters on the raw trade date. */}
            <label className="field">
              <span>Linked Trade (same day)</span>
              <select
                value={v.linkage.tradeId}
                onChange={(e) => setV((p) => ({ ...p, linkage: { tradeId: e.target.value } }))}
              >
                <option value="">(none)</option>
                {/* 🚨 A LINK ALREADY SAVED SURVIVES A MENU THAT CANNOT OFFER IT.
                    The read can fail, or the trade can move to another day once
                    the day-boundary mode changes. Without this the select finds
                    no matching option, falls back to "(none)", and the next save
                    writes that blank over a real link — silently, with the user
                    looking at a form that appears to say what they meant. */}
                {v.linkage.tradeId && !sameDay.some((t) => t.id === v.linkage.tradeId) ? (
                  <option value={v.linkage.tradeId}>
                    (linked trade — not on this day)
                  </option>
                ) : null}
                {sameDay.map((t) => (
                  <option key={t.id} value={t.id}>
                    {[t.contract ?? '—', t.timeIn ?? '', t.netPnl == null ? '' : moneyPlain(t.netPnl, 2)]
                      .filter(Boolean).join(' · ')}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Taken?</span>
              <select
                value={v.identity.taken ? 'true' : 'false'}
                onChange={(e) => ident({ taken: e.target.value === 'true' })}
              >
                <option value="false">No (skipped)</option>
                <option value="true">Yes (taken)</option>
              </select>
            </label>
          </div>

          <h4 className="bo-sec">CONTEXT (planned)</h4>
          {/* 🚨 ALL THREE ZONES ARE FREE TEXT. Josh's real stop zone is
              "7477-7475" — descending — so a number input, a range parser or an
              ordering rule would reject the way he actually writes them. */}
          <div className="form-grid cols-3">
            <label className="field">
              <span>Entry Zone</span>
              <input value={v.context.entryZone} placeholder="4520-4523" onChange={(e) => ctx({ entryZone: e.target.value })} />
            </label>
            <label className="field">
              <span>Stop Zone</span>
              <input value={v.context.stopZone} placeholder="4517" onChange={(e) => ctx({ stopZone: e.target.value })} />
            </label>
            <label className="field">
              <span>Target Zone</span>
              <input value={v.context.targetZone} placeholder="4540" onChange={(e) => ctx({ targetZone: e.target.value })} />
            </label>
            <label className="field">
              <span>Est R:R</span>
              <input value={v.context.estRR} placeholder="3.5" onChange={(e) => ctx({ estRR: e.target.value })} />
            </label>
            <label className="field">
              <span>Est Risk $</span>
              <input
                type="number" value={v.context.estRiskDollar}
                onChange={(e) => ctx({ estRiskDollar: Number(e.target.value) || 0 })}
              />
            </label>
          </div>

          <h4 className="bo-sec">REFLECTION</h4>
          <div className="form-grid cols-1">
            <label className="field">
              <span>Why taken / why skipped</span>
              <textarea rows={3} value={v.reflection.reason} onChange={(e) => refl({ reason: e.target.value })} />
            </label>
            <label className="field">
              <span>What would I do different</span>
              <textarea rows={2} value={v.reflection.wouldDoDifferent} onChange={(e) => refl({ wouldDoDifferent: e.target.value })} />
            </label>
          </div>

          <h4 className="bo-sec">SCREENSHOT</h4>
          <ScreenshotGrid
            files={v.screenshots}
            prefix={`${v.identity.date || todayIso()}-bestopp-${v.identity.contract || 'x'}`}
            onChange={(next) => setV((s) => ({ ...s, screenshots: next }))}
          />

          {err ? <p className="bo-error" role="alert">{err}</p> : null}
        </div>

        <footer className="modal-foot">
          {/* ⚠ DELETE IS EDIT-ONLY AND SITS LEFT — beta's footer exactly. A new,
              unsaved opp has nothing to delete, and offering it would be a button
              that either errors or silently does nothing. */}
          {!isNew ? (
            confirmDel ? (
              <span className="row gap-8">
                <span className="muted">Delete this best opp?</span>
                <button
                  className="btn danger" disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    const e = await onDelete(v.id);
                    setBusy(false);
                    if (e) { setErr(e); setConfirmDel(false); return; }
                    discardDraft();
                  }}
                >
                  Yes, delete
                </button>
                <button className="btn" onClick={() => setConfirmDel(false)}>Keep</button>
              </span>
            ) : (
              <button className="btn danger" onClick={() => setConfirmDel(true)}>Delete</button>
            )
          ) : null}
          <div className="grow" />
          <button className="btn" onClick={cancel}>Cancel</button>
          <button
            className="btn primary" disabled={busy}
            onClick={async () => {
              setBusy(true);
              setErr(null);
              const e = await onSave(v);
              setBusy(false);
              // ⚠ ONLY ON SUCCESS. The refusal path below keeps the modal open on
              // purpose, so the draft has to survive it.
              if (!e) discardDraft();
              // ⚠ THE MODAL STAYS OPEN ON A REFUSAL. An Est R:R of "3.5-4" cannot
              // be stored, and closing here would take the trader's typing with
              // it — which is exactly the silent loss this refusal exists to stop.
              if (e) setErr(e);
            }}
          >
            Save
          </button>
        </footer>
      </div>
    </div>
  );
}
